import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Input,
  Row,
  Select,
  Table,
  Tag,
  notification,
} from "antd";
import { RedoOutlined } from "@ant-design/icons";
import GetInvoiceEventLogs from "../../functions/admin/getInvoiceEventLogs";
import { invoicePaidAt } from "../../functions/admin/invoiceControlRow";
import moneyFormat from "../../functions/utils/moneyFormat";
import {
  formatInUlaanbaatar,
  formatUbCreatedAtRange,
} from "../../functions/utils/ulaanbaatarTime";
import TablePagination from "components/pagination/tablePagination";
import { bg_button, bg_shadow } from "constant/css";

const { RangePicker } = DatePicker;

const ACTION_LABELS = {
  set_paid: "Төлөгдсөн болгосон",
  cb_job_ok: "Callback job: амжилттай",
  cb_job_r1: "Callback job: retry 1",
  cb_job_r2: "Callback job: retry дууссан",
  cb_job_skip: "Callback job: алгассан",
};

const ACTION_OPTIONS = Object.entries(ACTION_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const timeCell = (value) =>
  value ? formatInUlaanbaatar(value, "YYYY-MM-DD HH:mm") : "—";

function buildEventLogQuery({
  page,
  pageSize,
  merchantId,
  invoiceId,
  invoiceStatus,
  action,
  createdAt,
}) {
  const parts = [`page=${page}`, `size=${pageSize}`];
  if (merchantId) parts.push(`merchantId=${encodeURIComponent(merchantId)}`);
  if (invoiceId) parts.push(`invoiceId=${encodeURIComponent(invoiceId)}`);
  if (invoiceStatus) {
    parts.push(`invoiceStatus=${encodeURIComponent(invoiceStatus)}`);
  }
  if (action) parts.push(`action=${encodeURIComponent(action)}`);
  const range = formatUbCreatedAtRange(createdAt);
  if (range) parts.push(`createdAt=${encodeURIComponent(range)}`);
  return `&${parts.join("&")}`;
}

/**
 * Admin «Пос нэхэмжлэл» — invoiceEventLog + холбоотой invoice (унших).
 */
const InvoiceEventLogTab = ({ merchants, active }) => {
  const [merchantId, setMerchantId] = useState(null);
  const [invoiceIdDraft, setInvoiceIdDraft] = useState("");
  const [invoiceIdFilter, setInvoiceIdFilter] = useState("");
  const [invoiceStatus, setInvoiceStatus] = useState(null);
  const [actionFilter, setActionFilter] = useState(null);
  const [createdAt, setCreatedAt] = useState(null);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [paginationData, setPaginationData] = useState({
    pageSize: 20,
    total: 0,
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const load = async () => {
      setFetching(true);
      try {
        const query = buildEventLogQuery({
          page,
          pageSize,
          merchantId,
          invoiceId: invoiceIdFilter,
          invoiceStatus,
          action: actionFilter,
          createdAt,
        });
        const response = await GetInvoiceEventLogs(query);
        if (cancelled || !response?.success) return;
        setData(response.data || []);
        const p = response.pagination || {};
        setPaginationData({
          pageSize: p.pageSize || pageSize,
          total: p.total ?? 0,
        });
      } catch (err) {
        if (!cancelled) {
          setData([]);
          notification.error({
            message:
              err.response?.data?.message ||
              err.message ||
              "invoiceEventLog авахад алдаа гарлаа",
          });
        }
      } finally {
        if (!cancelled) setFetching(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [
    active,
    page,
    pageSize,
    merchantId,
    invoiceStatus,
    actionFilter,
    createdAt,
    invoiceIdFilter,
    refreshKey,
  ]);

  const changePage = (nextPage) => setPage(nextPage);
  const changeSize = (_current, size) => {
    setPage(1);
    setPageSize(size);
  };

  const applyFilters = () => {
    setPage(1);
    setInvoiceIdFilter(String(invoiceIdDraft || "").trim());
    setRefreshKey((k) => k + 1);
  };

  const columns = useMemo(
    () => [
      {
        title: "№",
        key: "index",
        width: 48,
        render: (_v, _r, index) => (page - 1) * pageSize + index + 1,
      },
      {
        title: "Үйлдлийн цаг",
        dataIndex: "createdAt",
        key: "eventAt",
        width: 150,
        fixed: "left",
        render: (v) => timeCell(v),
      },
      {
        title: "Үйлдэл",
        dataIndex: "action",
        key: "action",
        width: 200,
        ellipsis: true,
        render: (action) => ACTION_LABELS[action] || action || "—",
      },
      {
        title: "Нэхэмжлэл",
        key: "invoiceId",
        width: 88,
        render: (_v, row) => row.invoiceId ?? row.invoice?.id ?? "—",
      },
      {
        title: "Төлөв",
        key: "invoiceStatus",
        width: 100,
        align: "center",
        render: (_v, row) => {
          const status = row.invoice?.status;
          if (!status) return "—";
          return status === "PAID" ? (
            <Tag color="cyan">Төлөгдсөн</Tag>
          ) : (
            <Tag>Төлөгдөөгүй</Tag>
          );
        },
      },
      {
        title: "Төлөгдсөн цаг",
        key: "paidAt",
        width: 150,
        responsive: ["md"],
        render: (_v, row) =>
          row.invoice ? timeCell(invoicePaidAt(row.invoice)) : "—",
      },
      {
        title: "Шинэчлэгдсэн",
        key: "invoiceUpdatedAt",
        width: 140,
        responsive: ["lg"],
        render: (_v, row) => timeCell(row.invoice?.updatedAt),
      },
      {
        title: "Дүн",
        key: "amount",
        width: 110,
        align: "right",
        responsive: ["sm"],
        render: (_v, row) => {
          const amount = row.invoice?.amount;
          return amount != null ? moneyFormat(amount) : "—";
        },
      },
      {
        title: "Мерчант",
        key: "merchant",
        width: 160,
        ellipsis: true,
        responsive: ["md"],
        render: (_v, row) =>
          row.invoice?.merchant?.name ||
          merchants?.find((m) => m.value === row.merchantId)?.label ||
          row.merchantId ||
          "—",
      },
      {
        title: "Тайлбар",
        key: "description",
        ellipsis: true,
        responsive: ["xl"],
        render: (_v, row) => row.invoice?.description || "—",
      },
    ],
    [merchants, page, pageSize]
  );

  return (
    <div>
      <Alert
        type="info"
        showIcon
        className="mb-3"
        message="invoiceEventLog — нэхэмжлэлийн түүх"
        description="Зөвхөн бүртгэл: invoiceEventLog мөр + холбоотой invoice-ийн одоогийн төлөв. Эндээс төлбөр/callback ажиллуулахгүй."
      />
      <Row
        className="invoice-control-search"
        style={{ gap: 10, marginBottom: 12, flexWrap: "wrap" }}
      >
        <RangePicker
          size="small"
          value={createdAt}
          onChange={(dates) => {
            setPage(1);
            setCreatedAt(
              dates && dates[0] && dates[1] ? dates : null
            );
          }}
          allowClear
          placeholder={["Үйлдлийн огноо", "Дуусах"]}
        />
        <Select
          size="small"
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Бизнесийн нэр"
          style={{ width: 220 }}
          options={merchants}
          value={merchantId || undefined}
          onChange={(v) => {
            setPage(1);
            setMerchantId(v || null);
          }}
        />
        <Select
          size="small"
          allowClear
          placeholder="Invoice төлөв"
          style={{ minWidth: 140 }}
          options={[
            { value: "PAID", label: "Төлөгдсөн" },
            { value: "NOT_PAID", label: "Төлөгдөөгүй" },
          ]}
          value={invoiceStatus}
          onChange={(v) => {
            setPage(1);
            setInvoiceStatus(v);
          }}
        />
        <Select
          size="small"
          allowClear
          placeholder="Үйлдэл"
          style={{ minWidth: 200 }}
          options={ACTION_OPTIONS}
          value={actionFilter}
          onChange={(v) => {
            setPage(1);
            setActionFilter(v);
          }}
        />
        <Input
          size="small"
          placeholder="Нэхэмжлэлийн код"
          style={{ width: 140 }}
          value={invoiceIdDraft}
          onChange={(e) => setInvoiceIdDraft(e.target.value)}
          onPressEnter={applyFilters}
        />
        <Button
          type="primary"
          size="small"
          className={`${bg_button} ${bg_shadow} rounded-xl`}
          icon={<RedoOutlined />}
          onClick={applyFilters}
        >
          Шүүх
        </Button>
      </Row>
      <div className="tableContainer">
        <Table
          rowKey="id"
          className="invoice-control-table"
          columns={columns}
          dataSource={data}
          loading={fetching}
          pagination={false}
          size="small"
          scroll={{ x: "max-content", y: "calc(100vh - 38vh)" }}
        />
        <TablePagination
          paginationData={paginationData}
          page={page}
          changePage={changePage}
          changeSize={changeSize}
        />
      </div>
    </div>
  );
};

export default InvoiceEventLogTab;
